import { useEffect, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import type { I18nText } from "@/api/types";

export const tx = (t: I18nText | undefined, lang: string) => (t ? t[lang] ?? t.el ?? t.en ?? "" : "");

export function Button({ variant = "primary", className = "", ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "danger" }) {
  const base = "rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";
  const v = variant === "primary" ? "bg-brand text-white" : variant === "danger" ? "bg-red-600 text-white" : "border text-gray-700 hover:bg-gray-50";
  return <button {...p} className={`${base} ${v} ${className}`} />;
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-xs text-gray-500">{hint}</span>}
    </label>
  );
}

export function Input(p: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...p} className={`w-full rounded-lg border px-3 py-1.5 text-sm ${p.className ?? ""}`} />;
}

export function Textarea(p: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...p} className={`w-full rounded-lg border px-3 py-1.5 text-sm ${p.className ?? ""}`} />;
}

export function Select(p: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...p} className={`w-full rounded-lg border px-3 py-1.5 text-sm bg-white ${p.className ?? ""}`} />;
}

/** Centred modal (kept under its historical name so every call site stays the same). Escape and a click on the
 * backdrop close it; the body stays put while it is open. */
export function Drawer({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-2 sm:p-6" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`my-auto w-full ${wide ? "max-w-5xl" : "max-w-3xl"} rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" className="rounded px-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800" onClick={onClose} aria-label="close">✕</button>
        </div>
        <div className="max-h-[80vh] space-y-4 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

/** Settings block: title + explanation on the left, fields on the right (full width, stacks on phones). */
export function SettingsSection({ title, hint, children, actions }: { title: string; hint?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="grid gap-4 rounded-xl border bg-white p-5 lg:grid-cols-[260px_1fr]">
      <div><h2 className="font-semibold">{title}</h2>{hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}{actions && <div className="mt-3">{actions}</div>}</div>
      <div className="min-w-0 space-y-3">{children}</div>
    </section>
  );
}

export function Badge({ children, tone = "gray" }: { children: ReactNode; tone?: "gray" | "green" | "red" | "amber" | "blue" }) {
  const c = { gray: "bg-gray-100 text-gray-700", green: "bg-green-100 text-green-800", red: "bg-red-100 text-red-800", amber: "bg-amber-100 text-amber-800", blue: "bg-blue-100 text-blue-800" }[tone];
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${c}`}>{children}</span>;
}
